import React, { useState, useEffect } from 'react';
import './AddressFormModal.css';

const AddressFormModal = ({ isOpen, onClose, onSave, address = null, user, addressesCount = 0 }) => {
  const [formData, setFormData] = useState({
    type: 'home',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Pakistan',
    isDefault: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        type: address.type || 'home',
        addressLine1: address.addressLine1 || address.address || '',
        addressLine2: address.addressLine2 || '',
        city: address.city || '',
        state: address.state || '',
        postalCode: address.postalCode || '',
        country: address.country || 'Pakistan',
        isDefault: address.isDefault || false
      });
    } else {
      // For new address, if it's the first address (count is 0), set as default
      // For subsequent addresses, isDefault will be false
      setFormData({
        type: 'home',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Pakistan',
        isDefault: addressesCount === 0 // Only first address is default
      });
    }
    setErrors({});
  }, [address, isOpen, addressesCount]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State/Province is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving address:', error);
      setErrors({ submit: error.message || 'Failed to save address' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="address-modal-overlay" onClick={onClose}>
      <div className="address-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="address-modal-header">
          <h2>{address ? 'Edit Shipping Address' : 'Add Shipping Address'}</h2>
          <button className="address-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="address-form-help">
          <p>💡 <strong>Note:</strong> Address Line 1, City, and State/Province are required. Other fields are optional.</p>
        </div>

        <form onSubmit={handleSubmit} className="address-form">
          {errors.submit && (
            <div className="address-form-error">
              {errors.submit}
            </div>
          )}

          <div className="address-form-group">
            <label htmlFor="type">Address Type *</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={errors.type ? 'error' : ''}
            >
              <option value="home">Home</option>
              <option value="work">Work</option>
              <option value="other">Other</option>
            </select>
            {errors.type && <span className="error-message">{errors.type}</span>}
          </div>

          <div className="address-form-group">
            <label htmlFor="addressLine1">Address Line 1 *</label>
            <input
              type="text"
              id="addressLine1"
              name="addressLine1"
              value={formData.addressLine1}
              onChange={handleChange}
              placeholder="Street address, house number"
              className={errors.addressLine1 ? 'error' : ''}
              required
            />
            {errors.addressLine1 && <span className="error-message">{errors.addressLine1}</span>}
          </div>

          <div className="address-form-group">
            <label htmlFor="addressLine2">Address Line 2</label>
            <input
              type="text"
              id="addressLine2"
              name="addressLine2"
              value={formData.addressLine2}
              onChange={handleChange}
              placeholder="Apartment, suite, etc. (optional)"
            />
          </div>

          <div className="address-form-row">
            <div className="address-form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                className={errors.city ? 'error' : ''}
                required
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div className="address-form-group">
              <label htmlFor="state">State/Province *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className={errors.state ? 'error' : ''}
                required
              >
                <option value="">Select Province *</option>
                <option value="Punjab">Punjab</option>
                <option value="Sindh">Sindh</option>
                <option value="Khyber Pakhtunkhwa">Khyber Pakhtunkhwa (KPK)</option>
                <option value="Balochistan">Balochistan</option>
                <option value="Gilgit-Baltistan">Gilgit-Baltistan</option>
                <option value="Azad Jammu and Kashmir">Azad Jammu and Kashmir (AJK)</option>
                <option value="Islamabad Capital Territory">Islamabad Capital Territory (ICT)</option>
              </select>
              {errors.state && <span className="error-message">{errors.state}</span>}
            </div>
          </div>

          <div className="address-form-row">
            <div className="address-form-group">
              <label htmlFor="postalCode">Postal Code</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="Postal code (optional)"
              />
            </div>

            <div className="address-form-group">
              <label htmlFor="country">Country</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                disabled
                style={{ cursor: 'not-allowed', opacity: 0.7 }}
              >
                <option value="Pakistan">Pakistan</option>
              </select>
            </div>
          </div>

          {/* Only show default checkbox for first address */}
          {addressesCount === 0 && (
            <div className="address-form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleChange}
                />
                <span>☑️ Set as default shipping address</span>
              </label>
            </div>
          )}

          <div className="address-form-actions">
            <button
              type="button"
              className="address-btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="address-btn-save"
              disabled={loading}
            >
              {loading ? 'Saving...' : address ? 'Update Address' : 'Add Address'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressFormModal;

